import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";

import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { IAuthContext } from "@/contexts/AuthContext";
import { Navbar } from "@/components/layout/Navbar";
import { FeatureFlags } from "@/integrations/launch-darkly/utils";
import { NotFoundRedirect } from "@/pages/NotFound";

const PUBLIC_PATHS = ["/login", "/login-link", "/login-link-modal"];
const PUBLIC_PATH_PREFIXES = ["/innovation-days-demo", "/faq"];
const ENTRY_PATHS = ["/", "/login"];
const DEFAULT_LOGGED_IN_PATH = "/home";

function isPublicPath(pathname: string) {
  return (
    PUBLIC_PATHS.includes(pathname) ||
    PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

export const Route = createRootRouteWithContext<{
  auth: IAuthContext;
  featureFlags: FeatureFlags;
}>()({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: ({ context, location, search }) => {
    const { isLoggedIn, loading } = context.auth;
    if (loading) return;

    if (isLoggedIn) {
      if (ENTRY_PATHS.includes(location.pathname)) {
        throw redirect({
          href: search.redirect || DEFAULT_LOGGED_IN_PATH,
          replace: true,
        });
      }
      return;
    }

    if (isPublicPath(location.pathname)) return;

    throw redirect({
      to: "/login",
      search: { redirect: location.href },
      replace: true,
    });
  },
  component: RootComponent,
  notFoundComponent: NotFoundRedirect,
});

function RootComponent() {
  const location = useLocation();

  if (location.pathname.startsWith("/faq")) {
    return <Outlet />;
  }

  return (
    <div className="bg-secondary-50 min-h-screen">
      <Navbar />
      <div className="max-w-[100rem] mx-auto px-[0.25rem] lg:px-[1.5rem] md:px-[0.5rem]">
        <Outlet />
      </div>
      {import.meta.env.VITE_ENVIRONMENT === "local" && (
        <TanStackRouterDevtools />
      )}
    </div>
  );
}
